import {
  PlacementXAnchor,
  PlacementYAnchor,
  TextAlignment,
  TextVerticalAlignment,
} from "building-types/annotation/annotationTypesEnum";
import type { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import {
  sheetPolylineGeometryNew,
  sheetCircleGeometryNew,
  sheetArcGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import { ONE_INCH, ZERO_INCHES } from "common/constants/scaleFactors";
import type { SheetInches, SheetPoints } from "common/types/nominalNumbersTypes";
import {
  numAdd,
  numSub,
  percentNew,
  sheetInchesNew,
  sheetPixelsNew,
  sheetPixelsToInches,
  sheetPointsNew,
} from "common/utils/nominalNumbers";
import { mat3Dot, mat3Mult, mat3Translate } from "geom/mat3";
import { polyLine2Transform, polyLine2ByTwoPoints } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { DEFAULT_MEDIUM_FONT_SIZE } from "pdf/planFonts";
import colors from "ui/styles/colors";

export interface ElevMarker {
  levelName: string;
  elevationText?: string;
  zLevel: SheetInches;
  dodgeOffset?: SheetInches;
  priority: number;
  category?: SheetGeometryCategory;
}

export type ElevMarkersTagProps = {
  x: SheetInches;
  minX: SheetInches;
  maxX: SheetInches;
  elevMarkers: ElevMarker[];
  category?: SheetGeometryCategory;
  textStrokeWidth?: SheetPoints;
  offsetLimit?: SheetInches;
};

export const elevMarkersTagDefault: ElevMarkersTagProps = {
  x: ZERO_INCHES,
  minX: sheetInchesNew(-2),
  maxX: sheetInchesNew(2),
  textStrokeWidth: sheetPointsNew(2),
  elevMarkers: [
    {
      levelName: "FIRST FLOOR",
      elevationText: `EL. 0'-0"`,
      zLevel: ZERO_INCHES,
      priority: 100,
    },
    {
      levelName: "SECOND FLOOR",
      elevationText: `EL. 1'-0"`,
      zLevel: sheetInchesNew(-0.1),
      priority: 99,
    },
    {
      levelName: "THIRD FLOOR",
      elevationText: `EL. 2'-0"`,
      zLevel: sheetInchesNew(-0.2),
      priority: 99,
    },
    {
      levelName: "FOURTH FLOOR",
      elevationText: `EL. 5'-0"`,
      zLevel: sheetInchesNew(-0.5),
      priority: 99,
    },
    {
      levelName: "FIFTH FLOOR",
      elevationText: `EL. 20'-0"`,
      zLevel: sheetInchesNew(-2),
      priority: 99,
    },
  ],
  offsetLimit: sheetInchesNew(0.5),
};

// Convert pixels to inches, to keep the appearance of the tag the same as when we used SVG.
function pix(n: number): SheetInches {
  return sheetPixelsToInches(sheetPixelsNew(n));
}

export function generateElevMarkersTagSheetGeometries(inProps: ElevMarkersTagProps): SheetGeometry[] {
  const props = { ...elevMarkersTagDefault, ...inProps };
  const result: SheetGeometry[] = [];

  const elevMarkers = props.elevMarkers;
  const minXS = props.minX;
  const maxXS = props.maxX;

  // sort tags by level then priority
  elevMarkers.sort(sortMarker);

  // space tags evenly adding diagonal leaders to make up the difference
  const offsetLimit = props.offsetLimit ?? elevMarkersTagDefault.offsetLimit ?? ZERO_INCHES;
  let currentTopClear: SheetInches | undefined;
  for (const elevMarker of elevMarkers) {
    const zLevelS = elevMarker.zLevel;
    elevMarker.dodgeOffset = ZERO_INCHES;
    const offset = currentTopClear !== undefined ? numSub(currentTopClear, zLevelS) : undefined;
    if (offset !== undefined && offset < offsetLimit) {
      elevMarker.dodgeOffset = numSub(elevMarker.dodgeOffset, numSub(offsetLimit, offset));
    }
    currentTopClear = numAdd(zLevelS, elevMarker.dodgeOffset);

    if (elevMarker.category !== props.category) {
      continue;
    }

    const xPlacement = numAdd(maxXS, ONE_INCH);
    const xLeftPlacement = numSub(minXS, numAdd(xPlacement, sheetInchesNew(2)));

    const transform = mat3Translate(xPlacement + (props.x ?? 0), zLevelS);

    const dodgeOffsetPx = elevMarker.dodgeOffset ?? ZERO_INCHES;
    const transformCircle = mat3Mult(transform, mat3Translate(0, dodgeOffsetPx));
    const [zeroX, zeroY] = mat3Dot(transformCircle, 0, 0);
    const radius = pix(14);
    const rotation = 0;

    const fontSize = DEFAULT_MEDIUM_FONT_SIZE;
    const transformText = mat3Mult(transformCircle, mat3Translate(pix(30), pix(-6)));
    const [xText, yText] = mat3Dot(transformText, 0, 0);
    const [x2Text, y2Text] = mat3Dot(transformText, 0, fontSize);

    const strokeWidth = sheetPointsNew(2);
    const textStrokeColor = colors.white;

    const fillLight = colors.white;

    result.push(
      sheetPolylineGeometryNew({
        paths: [
          polyLine2Transform(
            polyLine2New([-pix(28), dodgeOffsetPx, dodgeOffsetPx - pix(28), 0, dodgeOffsetPx - pix(28) - pix(20), 0]),
            transform,
          ),
        ],
        dash: false,
        fill: false,
      }),

      sheetPolylineGeometryNew({
        paths: [polyLine2Transform(polyLine2ByTwoPoints(xLeftPlacement, 0, dodgeOffsetPx - pix(28), 0), transform)],
        dash: true,
        dashLength: [60, 15, 13.5, 15].map(sheetPointsNew),
        fill: false,
      }),

      sheetCircleGeometryNew({
        x: zeroX as SheetInches,
        y: zeroY as SheetInches,
        radius,
        dash: false,
        fill: false,
        fillColor: fillLight,
        fillOpacity: percentNew(1),
      }),

      sheetPolylineGeometryNew({
        paths: [polyLine2Transform(polyLine2ByTwoPoints(-pix(28), 0, pix(200), 0), transformCircle)],
        dash: false,
      }),

      sheetArcGeometryNew({
        x: zeroX as SheetInches,
        y: zeroY as SheetInches,
        xRadius: radius,
        yRadius: radius,
        startRad: Math.PI / 2 + rotation,
        endRad: Math.PI + rotation,
        clockwise: true,
        dash: false,
      }),

      sheetArcGeometryNew({
        x: zeroX as SheetInches,
        y: zeroY as SheetInches,
        xRadius: radius,
        yRadius: radius,
        startRad: rotation + (3 * Math.PI) / 2,
        endRad: rotation + 2 * Math.PI,
        clockwise: true,
        dash: false,
      }),

      sheetTextGeometryNew({
        x: xText,
        y: yText,
        text: elevMarker.levelName,
        strokeColor: textStrokeColor,
        strokeWidth,
        alignment: TextAlignment.LEFT,
        verticalAlignment: TextVerticalAlignment.BOTTOM,
        placementYAnchor: PlacementYAnchor.BOTTOM,
        placementXAnchor: PlacementXAnchor.LEFT,
      }),

      sheetTextGeometryNew({
        x: x2Text,
        y: y2Text,
        text: elevMarker.elevationText ?? "",
        strokeColor: textStrokeColor,
        strokeWidth,
        alignment: TextAlignment.LEFT,
        verticalAlignment: TextVerticalAlignment.TOP,
        placementYAnchor: PlacementYAnchor.TOP,
        placementXAnchor: PlacementXAnchor.LEFT,
      }),
    );
  }
  return result;
}

function sortMarker(a: ElevMarker, b: ElevMarker): number {
  if (a.zLevel === b.zLevel) {
    return a.priority - b.priority;
  }
  return b.zLevel - a.zLevel;
}
